import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FormControl,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { useAuth } from '../../contexts/AuthContext';
import { useView } from '../../contexts/ViewContext';
import { supabase } from '../../config/supabase';

/**
 * ClinicSelector Component
 * Shows current clinic affiliation with dropdown to switch clinics
 * Available to super admins, physicians, and clinic admins
 * When a clinic is selected, navigates to that clinic's dashboard
 * 
 * @param {object} sx - Additional styling
 * @param {function} onSelection - Callback fired when a selection is made
 */
const ClinicSelector = ({ sx = {}, onSelection }) => {
  const { tenant, isSuperAdmin, profile } = useAuth();
  const { viewMode, enterClinicViewMode, exitViewMode } = useView();
  const navigate = useNavigate();
  const location = useLocation();
  const [clinics, setClinics] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState(tenant?.id || '');
  const [loading, setLoading] = useState(false);

  // Check if user is physician or admin
  const isPhysician = profile?.tenant_role === 'physician' || profile?.platform_role === 'global_physician';
  const isAdmin = isSuperAdmin || profile?.tenant_role === 'clinic_admin';
  const canViewClinics = isSuperAdmin || isPhysician || isAdmin;

  // Fetch all clinics (for super admins/physicians/admins) or just user's clinic
  useEffect(() => {
    async function fetchClinics() {
      setLoading(true);
      try {
        if (isSuperAdmin) {
          // Super admins see all clinics (excluding platform tenant)
          const { data, error } = await supabase
            .from('tenants')
            .select('id, name, tenant_type')
            .neq('tenant_type', 'platform')
            .order('name');

          if (error) throw error;
          setClinics(data || []);
        } else if (isPhysician || isAdmin) {
          // Physicians and clinic admins see all clinics (excluding platform tenant)
          // Platform tenant is for onboarding only, not operational work
          const { data, error } = await supabase
            .from('tenants')
            .select('id, name, tenant_type')
            .neq('tenant_type', 'platform')
            .order('name');

          if (error) throw error;
          setClinics(data || []);
        } else {
          // Regular users see only their clinic
          if (tenant) {
            setClinics([{ id: tenant.id, name: tenant.name }]);
          }
        }

        // Add Platform Admin option for super admins
        if (isSuperAdmin) {
          setClinics(prev => [
            { id: 'platform', name: '🏢 Platform Admin' },
            ...prev.filter(c => c.id !== 'platform')
          ]);
        }

        // Set initial selection based on view mode or tenant
        // Priority: viewMode > tenant
        if (viewMode?.active && viewMode?.tenantId) {
          // In view mode - use the viewed tenant
          setSelectedClinic(viewMode.tenantId);
        } else if (location.pathname.startsWith('/admin') && isSuperAdmin) {
          // If on admin route and super admin, select platform
          setSelectedClinic('platform');
        } else if (tenant?.id) {
          // Normal mode - use user's actual tenant
          setSelectedClinic(tenant.id);
        }
      } catch (error) {
        console.error('Failed to fetch clinics:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchClinics();
  }, [tenant, isSuperAdmin, isPhysician, isAdmin, viewMode, location.pathname]);

  const handleClinicChange = async (event) => {
    const clinicId = event.target.value;
    const clinicName = clinics.find(c => c.id === clinicId)?.name || '';

    setSelectedClinic(clinicId);

    // Notify parent component of selection (e.g., to close sidebar)
    if (onSelection) {
      onSelection();
    }

    // For super admins, physicians, and admins, entering clinic view mode switches to that clinic's view
    if ((isSuperAdmin || isPhysician || isAdmin) && clinicId) {
      // Handle Platform Admin selection
      if (clinicId === 'platform') {
        if (isSuperAdmin) {
          exitViewMode();
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        navigate('/admin', { replace: true });
        return;
      }

      // If selecting the user's own tenant, exit view mode (for super admins) or just navigate
      if (clinicId === tenant?.id) {
        if (isSuperAdmin) {
          exitViewMode();
          // Small delay to ensure state is saved
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        // Navigate back to admin dashboard or current dashboard
        if (location.pathname.includes('/admin')) {
          if (location.pathname !== '/admin') {
            navigate('/admin', { replace: true });
          } else {
            navigate(0);
          }
        } else if (location.pathname.includes('/clinic-dashboard')) {
          // Already on clinic dashboard, just refresh
          navigate(0);
        } else {
          // Navigate to appropriate dashboard based on role
          if (isSuperAdmin || isAdmin) {
            navigate('/admin', { replace: true });
          } else if (isPhysician) {
            navigate('/clinic-dashboard', { replace: true });
          }
        }
      } else {
        // Enter clinic view mode for the selected clinic (only super admins can use view mode)
        if (isSuperAdmin) {
          enterClinicViewMode(clinicId, clinicName);
          // Small delay to ensure state is saved to sessionStorage
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        // Navigate to clinic dashboard to show the clinic's view
        if (location.pathname !== '/clinic-dashboard') {
          navigate('/clinic-dashboard', { replace: true });
        } else {
          // Force reload if already on clinic-dashboard to refresh with new view mode
          navigate(0);
        }
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...sx }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  // Don't render if user can't view clinics or no clinics loaded yet
  if (!canViewClinics || clinics.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...sx }}>
      <BusinessIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <Select
          value={selectedClinic || ''}
          onChange={handleClinicChange}
          disabled={clinics.length <= 1}
          sx={{
            '& .MuiSelect-select': {
              py: 1,
              fontSize: '0.875rem',
              fontWeight: 500
            },
            '&.Mui-disabled': {
              '& .MuiSelect-select': {
                color: 'text.primary',
                WebkitTextFillColor: 'unset'
              }
            }
          }}
        >
          {clinics.map((clinic) => (
            <MenuItem key={clinic.id} value={clinic.id}>
              {clinic.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {clinics.length > 1 && (
        <Typography variant="caption" color="text.secondary">
          ({clinics.length} clinics)
        </Typography>
      )}
    </Box>
  );
};

export default ClinicSelector;
